import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {environment} from '../../../../../environments/environment';
import {INTENT_STORAGE_KEY, IntentService} from '../../../../core/service/intent.service';
import {INTENT_OPTIONS} from '../../../../shared/components/user-intent-modal/onboarding.config';
import {IconComponent} from '../../../../shared/components/icon/icon.component';

/**
 * Dev-only přepínač zaměření (intentu) v topbaru. Zapíná ho `environment.devIntentSwitcher`
 * (`environment.local.ts` a `environment.ts`) — v produkčním buildu se komponenta nevyrenderuje vůbec.
 *
 * Proč: vertikály (subjekty, kroky, recepty, launchpad) se řídí intentem uživatele a jediná
 * cesta, jak ho změnit, byla nová registrace nebo ruční zápis do DB. Přepínač uloží intent
 * stejným `POST /intent` jako registrační modál a pak SPA znovu načte — intent čte ~16 míst
 * jednorázově při inicializaci (`IntentService.getIntent()`), takže reload je jediný způsob,
 * jak je všechny spolehlivě přepnout, bez zavádění globálního reaktivního stavu jen kvůli
 * vývojářskému nástroji.
 *
 * Bez i18n záměrně: nikdy se nezobrazí uživateli, popisek je jen orientační pro vývojáře.
 */
@Component({
    selector: 'app-dev-intent-switcher',
    standalone: true,
    imports: [TranslatePipe, IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (enabled) {
            <label class="flex items-center gap-1 text-xs opacity-70" title="DEV: přepnout zaměření (intent) — uloží se přes API a stránka se znovu načte">
                <app-icon icon="ph:bug" size="sm" class="opacity-70"/>
                <select class="select select-ghost select-xs w-auto"
                        [disabled]="busy()"
                        (change)="onChange($any($event.target).value)">
                    <option value="" disabled [selected]="!current()">intent…</option>
                    @for (o of options; track o.id) {
                        <!-- [selected] na option, ne [value] na select — options vznikají až po nastavení hodnoty selectu -->
                        <option [value]="o.id" [selected]="o.id === current()">{{ o.titleKey | translate }}</option>
                    }
                </select>
            </label>
        }
    `,
})
export class DevIntentSwitcherComponent implements OnInit {

    readonly enabled = environment.devIntentSwitcher === true;
    readonly options = INTENT_OPTIONS;
    readonly current = signal<string | null>(null);
    readonly busy = signal(false);

    private readonly intentService = inject(IntentService);

    ngOnInit(): void {
        if (!this.enabled) return;
        this.intentService.getIntent().subscribe({
            next: payload => this.current.set(payload?.intent ?? null),
            error: () => this.current.set(null),
        });
    }

    onChange(intent: string): void {
        if (!intent || intent === this.current() || this.busy()) return;
        this.busy.set(true);
        this.intentService.submitIntent(intent).subscribe({
            next: () => {
                // Stejný flag jako registrační flow — dashboard pak neotevře modál výběru intentu.
                localStorage.setItem(INTENT_STORAGE_KEY, 'true');
                this.current.set(intent);
                this.reloadPage();
            },
            error: () => this.busy.set(false),
        });
    }

    /** Seam pro test — v prohlížeči tvrdý reload, ať se všechny konzumenty intentu načtou znovu. */
    protected reloadPage(): void {
        location.reload();
    }
}
